import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import AuthService from '../Api/services/auth.service';
import type { AuthState, User, LoginCredentials, ExternalAuthCredentials, Permission } from '../Types/Auth/Auth.types';
import { toast } from 'react-toastify';

interface AuthContextType extends AuthState {
  login: (credentials: LoginCredentials) => Promise<void>;
  externalLogin: (credentials: ExternalAuthCredentials) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
  hasRole: (role: string | string[]) => boolean;
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
  });

  /**
   * Inicializar autenticación desde localStorage
   */
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = AuthService.getToken();
        const storedUser = AuthService.getStoredUser();

        if (token && storedUser) {
          // Debug: verificar usuario del localStorage
          if (process.env.NODE_ENV === 'development') {
            console.log('Usuario del localStorage al inicializar:', {
              storedUser,
              role: storedUser.role,
              roleName: storedUser.role?.name
            });
          }
          
          // Usar el usuario del localStorage directamente si está disponible
          // Esto evita problemas de CORS al inicializar
          setAuthState({
            user: storedUser,
            token,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          });
          
          // Intentar refrescar el usuario en segundo plano (sin bloquear)
          AuthService.getCurrentUser()
            .then((currentUser) => {
              if (process.env.NODE_ENV === 'development') {
                console.log('Usuario refrescado del backend:', {
                  currentUser,
                  role: currentUser.role,
                  roleName: currentUser.role?.name
                });
              }
              setAuthState((prev) => ({
                ...prev,
                user: currentUser,
              }));
              AuthService.setStoredUser(currentUser);
            })
            .catch((error) => {
              // Si falla por CORS u otro error, mantener el usuario del localStorage
              console.warn('No se pudo refrescar el usuario (puede ser CORS):', error);
            });
        } else {
          setAuthState((prev) => ({
            ...prev,
            isLoading: false,
          }));
        }
      } catch (error) {
        console.error('Error inicializando autenticación:', error);
        setAuthState({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false,
          error: 'Error al inicializar autenticación',
        });
      }
    };

    initializeAuth();
  }, []);

  /**
   * Login con usuario y contraseña
   */
  const login = useCallback(async (credentials: LoginCredentials) => {
    setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const response = await AuthService.login(credentials);
      
      // El token ya se guardó en auth.service.ts antes de llamar a /auth/users/me/
      // Solo actualizamos el estado aquí
      const user = response.user;
      
      if (user) {
        AuthService.setStoredUser(user);
        // Debug: verificar que el usuario tenga rol
        if (process.env.NODE_ENV === 'development') {
          console.log('Usuario después del login:', {
            user,
            role: user.role,
            roleName: user.role?.name
          });
        }
      } else {
        console.error('⚠️ Usuario no obtenido después del login');
        // Si no se obtuvo el usuario, limpiar el token y lanzar error
        AuthService.logout();
        throw new Error('No se pudo obtener la información del usuario');
      }

      setAuthState({
        user: user || null,
        token: response.access_token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });

      toast.success('Inicio de sesión exitoso');
    } catch (error: any) {
      const errorMessage = 
        error.response?.data?.detail || 
        error.response?.data?.message || 
        error.message || 
        'Error al iniciar sesión';
      
      setAuthState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));

      toast.error(errorMessage);
      throw error;
    }
  }, []);

  /**
   * Login externo (Firebase)
   */
  const externalLogin = useCallback(async (credentials: ExternalAuthCredentials) => {
    setAuthState((prev) => ({ ...prev, isLoading: true, error: null }));
    
    try {
      const response = await AuthService.externalAuth(credentials);
      
      // El token ya se guardó en auth.service.ts antes de llamar a /auth/users/me/
      const user = response.user;
      
      if (user) {
        AuthService.setStoredUser(user);
      } else {
        console.error('⚠️ Usuario no obtenido después del login externo');
        AuthService.logout();
        throw new Error('No se pudo obtener la información del usuario');
      }

      setAuthState({
        user: user,
        token: response.access_token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      });

      toast.success('Inicio de sesión exitoso');
    } catch (error: any) {
      const errorMessage = 
        error.response?.data?.detail || 
        error.response?.data?.message || 
        error.message || 
        'Error al iniciar sesión';
      
      setAuthState((prev) => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }));

      toast.error(errorMessage);
      throw error;
    }
  }, []);

  /**
   * Logout
   */
  const logout = useCallback(() => {
    AuthService.logout();
    setAuthState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
    });
    toast.success('Sesión cerrada exitosamente');
  }, []);

  /**
   * Refrescar información del usuario
   */
  const refreshUser = useCallback(async () => {
    try {
      const user = await AuthService.getCurrentUser();
      setAuthState((prev) => ({
        ...prev,
        user,
      }));
      AuthService.setStoredUser(user);
    } catch (error) {
      console.error('Error al refrescar usuario:', error);
      // Si falla, podría ser que el token expiró
      logout();
    }
  }, [logout]);

  /**
   * Verificar si el usuario tiene un rol específico
   * Normaliza los nombres de roles para manejar variaciones como "Administrador", "Admin", "admin"
   */
  const hasRole = useCallback((role: string | string[]): boolean => {
    if (!authState.user?.role?.name) return false;
    
    // Normalizar el nombre del rol del usuario
    // "Administrador" -> "admin", "Abogado" -> "lawyer", etc.
    const normalizeRoleName = (roleName: string): string => {
      const normalized = roleName.toLowerCase().trim();
      // Mapear variaciones comunes
      if (normalized.includes('admin') || normalized.includes('administrador')) {
        return 'admin';
      }
      if (normalized.includes('lawyer') || normalized.includes('abogado')) {
        return 'lawyer';
      }
      if (normalized.includes('client') || normalized.includes('cliente')) {
        return 'client';
      }
      if (normalized.includes('collector') || normalized.includes('colector')) {
        return 'collector';
      }
      return normalized;
    };
    
    const userRoleName = normalizeRoleName(authState.user.role.name);
    const rolesToCheck = Array.isArray(role) 
      ? role.map(r => normalizeRoleName(r))
      : [normalizeRoleName(role)];
    
    return rolesToCheck.includes(userRoleName);
  }, [authState.user]);

  /**
   * Verificar si el usuario tiene un permiso específico
   */
  const hasPermission = useCallback((permission: string): boolean => {
    if (!authState.user?.role?.permissions) return false;
    
    return authState.user.role.permissions.some(
      (perm: Permission) => perm.code?.toLowerCase() === permission.toLowerCase()
    );
  }, [authState.user]);

  /**
   * Verificar si el usuario tiene al menos uno de los permisos
   */
  const hasAnyPermission = useCallback((permissions: string[]): boolean => {
    return permissions.some(permission => hasPermission(permission));
  }, [hasPermission]);

  /**
   * Verificar si el usuario tiene todos los permisos
   */
  const hasAllPermissions = useCallback((permissions: string[]): boolean => {
    return permissions.every(permission => hasPermission(permission));
  }, [hasPermission]);

  const value: AuthContextType = {
    ...authState,
    login,
    externalLogin,
    logout,
    refreshUser,
    hasRole,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Hook para usar el contexto de autenticación
 */
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

